import { Button, ButtonProps } from "antd";
import React from "react";

type AppButtonVariant = "primary" | "secondary" | "subtle" | "text" | "outlined" | "icon";
type AppButtonSize = ButtonProps["size"];

export type AppButtonProps = Omit<ButtonProps, "variant"> & {
  variant?: AppButtonVariant;
};

const BASE_STYLE: React.CSSProperties = {
  borderRadius: 8,
  fontWeight: 500,
  minWidth: 80,
};

const SIZE_STYLES: Record<AppButtonSize, React.CSSProperties> = {
  small: {
    height: 28,
    fontSize: 12,
    paddingInline: 10,
  },
  middle: {
    height: 33,
    fontSize: 13,
    paddingInline: 12,
  },
  large: {
    height: 40,
    fontSize: 14,
    paddingInline: 16,
  },
};

const VARIANT_STYLES: Record<AppButtonVariant, React.CSSProperties> = {
  primary: {
    backgroundColor: "var(--blue-700)",
    border: "none",
    color: "white",
  },
  secondary: {
    backgroundColor: "var(--gray-300)",
    border: "none",
    color: "var(--gray-600)",
    boxShadow: "none",
    textShadow: "none",
  },
  subtle: {
    backgroundColor: "var(--gray-200)",
    border: "none",
    color: "var(--blue-700)",
    boxShadow: "none",
    textShadow: "none",
  },
  text: {
    backgroundColor: "transparent",
    border: "none",
    color: "var(--blue-700)",
    boxShadow: "none",
    textShadow: "none",
  },
  outlined: {
    backgroundColor: "transparent",
    border: "1px solid var(--blue-700)",
    color: "var(--blue-700)",
    boxShadow: "none",
    textShadow: "none",
  },
  icon: {
    backgroundColor: "transparent",
    border: "none",
    color: "var(--gray-500)",
    boxShadow: "none",
    textShadow: "none",
    minWidth: 28,
    width: 28,
    paddingInline: 4,
  },
};

const DISABLED_STYLE: React.CSSProperties = {
  opacity: 0.72,
  filter: "saturate(85%)",
};

const DISABLED_VARIANT_STYLES: Partial<Record<AppButtonVariant, React.CSSProperties>> = {
  outlined: {
    borderColor: "var(--gray-400)",
    color: "var(--gray-500)",
    backgroundColor: "var(--gray-100)",
  },
  text: {
    color: "var(--gray-500)",
  },
  icon: {
    color: "var(--gray-500)",
  },
};

export const AppButton: React.FC<AppButtonProps> = (props) => {
  const { variant = "primary", size = "middle", style, children, disabled, ...rest } = props;

  return (
    <Button
      disabled={disabled}
      {...rest}
      style={{
        ...BASE_STYLE,
        ...SIZE_STYLES[size],
        ...VARIANT_STYLES[variant],
        ...(disabled ? DISABLED_STYLE : {}),
        ...(disabled ? DISABLED_VARIANT_STYLES[variant] : {}),
        ...style,
      }}
    >
      {children}
    </Button>
  );
};
